import {
  $insertList,
  $isListItemNode,
  $isListNode,
  ListNode,
  ListType,
  SerializedListNode,
} from "@lexical/list"
import { $getNearestNodeOfType } from "@lexical/utils"
import {
  $applyNodeReplacement,
  $getSelection,
  $isRangeSelection,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  LexicalNodeReplacement,
  LexicalUpdateJSON,
  NodeKey,
  Spread,
} from "lexical"

export const ORDERED_LIST_STYLE_TYPES = [
  "decimal",
  "upper-alpha",
  "lower-alpha",
  "upper-roman",
  "lower-roman",
] as const

export type OrderedListStyleType = (typeof ORDERED_LIST_STYLE_TYPES)[number]

export type ListStyleValue = "bullet" | OrderedListStyleType

export const LIST_STYLE_OPTIONS: { value: ListStyleValue; label: string }[] = [
  { value: "bullet", label: "Bullets" },
  { value: "decimal", label: "1, 2, 3" },
  { value: "upper-alpha", label: "A, B, C" },
  { value: "lower-alpha", label: "a, b, c" },
  { value: "upper-roman", label: "I, II, III" },
  { value: "lower-roman", label: "i, ii, iii" },
]

const ORDERED_LIST_STYLE_TYPE_SET = new Set<string>(ORDERED_LIST_STYLE_TYPES)

const LIST_STYLE_TO_HTML_TYPE: Record<OrderedListStyleType, string> = {
  decimal: "1",
  "upper-alpha": "A",
  "lower-alpha": "a",
  "upper-roman": "I",
  "lower-roman": "i",
}

export type SerializedStyledListNode = Spread<
  {
    listStyleType?: OrderedListStyleType | null
  },
  SerializedListNode
>

function parseListStyleType(value: unknown): OrderedListStyleType | null {
  if (typeof value !== "string") return null
  if (!ORDERED_LIST_STYLE_TYPE_SET.has(value)) return null
  return value as OrderedListStyleType
}

function applyListStyleType(
  dom: HTMLElement,
  listStyleType: OrderedListStyleType | null
) {
  if (listStyleType) {
    dom.style.listStyleType = listStyleType
    const htmlType = LIST_STYLE_TO_HTML_TYPE[listStyleType]
    if (htmlType) {
      dom.setAttribute("type", htmlType)
    }
    return
  }

  dom.style.removeProperty("list-style-type")
  dom.removeAttribute("type")
}

export class StyledListNode extends ListNode {
  __listStyleType: OrderedListStyleType | null

  $config() {
    return this.config("styled-list", {
      $transform: (node: StyledListNode) => {
        $inheritListStyleTypeFromParent(node)
      },
      extends: ListNode,
    })
  }

  static clone(node: StyledListNode): StyledListNode {
    return new StyledListNode(node.__listType, node.__start, node.__key)
  }

  static importJSON(serializedNode: SerializedStyledListNode): StyledListNode {
    return new StyledListNode().updateFromJSON(serializedNode)
  }

  constructor(listType: ListType = "number", start: number = 1, key?: NodeKey) {
    super(listType, start, key)
    this.__listStyleType = null
  }

  afterCloneFrom(prevNode: this): void {
    super.afterCloneFrom(prevNode)
    this.__listStyleType = prevNode.__listStyleType
  }

  getListStyleType(): OrderedListStyleType | null {
    return this.getLatest().__listStyleType
  }

  setListStyleType(listStyleType: OrderedListStyleType | null): this {
    const writable = this.getWritable()
    writable.__listStyleType = listStyleType
    return writable
  }

  createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = super.createDOM(config, editor)
    if (this.__listType === "number") {
      applyListStyleType(dom, this.__listStyleType ?? "decimal")
    }
    return dom
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const replace = super.updateDOM(prevNode, dom, config)
    if (!replace && this.__listType === "number") {
      applyListStyleType(dom, this.__listStyleType ?? "decimal")
    }
    return replace
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedStyledListNode>
  ): this {
    return super
      .updateFromJSON(serializedNode)
      .setListStyleType(parseListStyleType(serializedNode.listStyleType))
  }

  exportJSON(): SerializedStyledListNode {
    const json = super.exportJSON()
    const listStyleType = this.getListStyleType()
    if (!listStyleType) {
      return json
    }
    return {
      ...json,
      listStyleType,
    }
  }
}

export function $createStyledListNode(
  listType?: ListType,
  start?: number
): StyledListNode {
  return $applyNodeReplacement(new StyledListNode(listType, start))
}

export function $isStyledListNode(
  node: LexicalNode | null | undefined
): node is StyledListNode {
  return node instanceof StyledListNode
}

function $ensureStyledList(list: ListNode): StyledListNode {
  if ($isStyledListNode(list)) return list

  const styled = $createStyledListNode(list.getListType(), list.getStart())
  list.replace(styled, true)
  return styled
}

function $getParentList(list: ListNode): ListNode | null {
  const parentItem = list.getParent()
  if (!$isListItemNode(parentItem)) return null
  const parentList = parentItem.getParent()
  return $isListNode(parentList) ? parentList : null
}

export function $inheritListStyleTypeFromParent(list: StyledListNode): void {
  if (list.getListType() !== "number") return
  if (list.getListStyleType() != null) return

  const parentList = $getParentList(list)
  if (!$isStyledListNode(parentList) || parentList.getListType() !== "number") {
    return
  }

  list.setListStyleType(parentList.getListStyleType() ?? "decimal")
}

function $setListStyleOnListTree(
  list: StyledListNode,
  value: OrderedListStyleType
): void {
  list.setListStyleType(value)
  for (const item of list.getChildren()) {
    if (!$isListItemNode(item)) continue
    for (const child of item.getChildren()) {
      if ($isListNode(child) && child.getListType() === "number") {
        $setListStyleOnListTree($ensureStyledList(child), value)
      }
    }
  }
}

export function $getListStyleValue(
  list: ListNode | null
): ListStyleValue | null {
  if (!list) return null
  if (list.getListType() === "bullet") return "bullet"
  if (list.getListType() !== "number") return null
  if ($isStyledListNode(list)) {
    return list.getListStyleType() ?? "decimal"
  }
  return "decimal"
}

export function $setListStyleValue(value: ListStyleValue): void {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) return

  if (value === "bullet") {
    $insertList("bullet")
    return
  }

  const currentList = $getNearestNodeOfType(
    selection.anchor.getNode(),
    ListNode
  )
  if (!currentList || currentList.getListType() !== "number") {
    $insertList("number")
  }

  const nextSelection = $getSelection()
  if (!$isRangeSelection(nextSelection)) return
  const list = $getNearestNodeOfType(nextSelection.anchor.getNode(), ListNode)
  if (!list) return
  $setListStyleOnListTree($ensureStyledList(list), value)
}

export const listNodeReplacement: LexicalNodeReplacement = {
  replace: ListNode,
  with: (node: ListNode) => {
    const replacement = new StyledListNode(node.__listType, node.__start)
    if ($isStyledListNode(node)) {
      replacement.__listStyleType = node.__listStyleType
    } else if (node.__listType === "number") {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const parentList = $getNearestNodeOfType(
          selection.anchor.getNode(),
          ListNode
        )
        if (
          $isStyledListNode(parentList) &&
          parentList.getListType() === "number"
        ) {
          replacement.__listStyleType =
            parentList.getListStyleType() ?? "decimal"
        }
      }
    }
    return replacement
  },
  withKlass: StyledListNode,
}

export const LIST_NODES = [StyledListNode, listNodeReplacement]
